/**
 * trackEvent(name, props) — the one function call sites use.
 *
 * Fire-and-forget: returns undefined, never throws, never needs `await`.
 *
 * Payload = common props + `stage` (schema) + auto-filled user props + `props`.
 * Auto-fill only touches properties the event declares in eventSchema.js or
 * lists as `dynamic` in moengageJourneyMap.js, so events with no properties
 * get common props only.
 *
 * Call sites pass the legacy name (EVENTS.*). Journey events go to MoEngage
 * under their common name (ekyc_page_loaded, …) with the handover's fixed
 * attributes; see moengageJourneyMap.js.
 */
import { config } from './config';
import { enqueue } from './moengageClient';
import { getSchema, validateEvent } from './eventSchema';
import { getCommonProps } from './commonProps';
import { getEsignContext, getUserContext } from './context';
import { getParityEntry, resolveMoEngageEvent } from './moengageJourneyMap';
import { EVENTS } from './events';
import { PII_KEYS, debugInfo, debugWarn, maskProps, recordForQa } from './logger';

/** Event property → user-context key. */
const AUTO_FILL = Object.freeze({
    mobile_number: 'mobile',
    email_id: 'email',
    full_name: 'fullName',
    // lead_filled_cdp uses `mobile` / `name` (handover §3)
    mobile: 'mobile',
    name: 'fullName',
    ekyc_user_id: 'ekycUserId',
    Client_Code: 'clientId',
});

/**
 * Web-only events that are NOT in the mobile handover (web_session_init_en,
 * ekyc_journey_started_en / _resumed_en / _completed_en / _dropped_en).
 * Not sent to MoEngage for now; call sites stay in place. Set to true to re-enable.
 */
const SEND_WEB_ONLY_EVENTS = false;
const WEB_ONLY_EVENTS = Object.freeze(Object.values(EVENTS.JOURNEY));

/** User attributes only in the handover — never sent on an event. */
export const USER_ATTRIBUTE_ONLY_KEYS = Object.freeze(['client_name', 'client_id', 'dob']);

const firedOnce = new Set();

const isDate = (v) => Object.prototype.toString.call(v) === '[object Date]';

/** Drop null/undefined/''/NaN, Date → ISO string, keep numbers as numbers. */
export const normalizeProps = (props) => {
    const out = {};
    Object.keys(props || {}).forEach((key) => {
        const v = props[key];
        if (v == null) return;
        if (typeof v === 'string' && v.trim() === '') return;
        if (typeof v === 'number' && Number.isNaN(v)) return;
        if (isDate(v)) {
            if (!Number.isNaN(v.getTime())) out[key] = v.toISOString();
            return;
        }
        out[key] = v;
    });
    return out;
};

const autoFilled = (schema, parity) => {
    if (!schema && !parity) return {};
    const ctx = getUserContext();
    const out = {};
    const declared = schema ? { ...schema.required, ...schema.optional } : {};
    if (parity) parity.dynamic.forEach((key) => { declared[key] = true; });
    if (schema && 'page_name' in declared && schema.pageName) out.page_name = schema.pageName;
    Object.keys(AUTO_FILL).forEach((prop) => {
        if (prop in declared) out[prop] = ctx[AUTO_FILL[prop]];
    });
    if ('esign_provider' in declared || 'esign_txn_id' in declared) {
        const esign = getEsignContext();
        if ('esign_provider' in declared) out.esign_provider = esign.provider;
        if ('esign_txn_id' in declared) out.esign_txn_id = esign.txnId;
    }
    return out;
};

/**
 * Remove PII keys from an event payload (in place) unless PII is allowed.
 * @param {Record<string, unknown>} payload
 * @param {boolean} [includePii]  defaults to INCLUDE_PII_IN_EVENTS
 * @returns {Record<string, unknown>}
 */
export const applyPiiPolicy = (payload, includePii = config.piiInEvents) => {
    if (!includePii) PII_KEYS.forEach((key) => { delete payload[key]; });
    return payload;
};

/**
 * Build the exact name + payload that would be sent. Exported for tests / QA.
 * @param {string} name  legacy name (EVENTS.*)
 * @returns {{ schema: object|null, name: string, legacyName: string, payload: Record<string, unknown> }}
 */
export const buildPayload = (name, props) => {
    const schema = getSchema(name);
    const parity = getParityEntry(name);
    const merged = normalizeProps({
        ...getCommonProps(),
        stage: schema ? schema.stage : undefined,
        ...autoFilled(schema, parity),
        // Empty caller values (e.g. a null `user.mobileNumber` after a reload)
        // must not wipe an auto-filled fallback, so they are dropped first.
        ...normalizeProps(props),
    });
    USER_ATTRIBUTE_ONLY_KEYS.forEach((key) => { delete merged[key]; });
    const resolved = resolveMoEngageEvent(name, merged);
    return { schema, name: resolved.name, legacyName: name, payload: resolved.props };
};

/** Debug-only: dynamic attributes the handover lists but the payload lacks. */
const missingDynamic = (legacyName, payload) => {
    const parity = getParityEntry(legacyName);
    return parity ? parity.dynamic.filter((key) => !(key in payload)) : [];
};

/**
 * Send one event. Safe to call from anywhere, any number of times.
 * @param {string} name   use EVENTS.* constants (legacy names)
 * @param {Record<string, unknown>} [props]
 * @returns {void}
 */
export const trackEvent = (name, props) => {
    try {
        if (!config.enabled) return;
        if (!SEND_WEB_ONLY_EVENTS && WEB_ONLY_EVENTS.includes(name)) return;
        const { schema, name: moeName, payload } = buildPayload(name, props);

        if (config.debug) {
            const problems = validateEvent(name, schema, payload);
            const missing = missingDynamic(name, payload);
            if (missing.length) problems.push(`missing handover attribute(s) ${missing.join(', ')}`);
            if (problems.length) debugWarn(`[MoEngage] ${name}:`, problems.join('; '));
        }

        applyPiiPolicy(payload);

        recordForQa(moeName, payload, config.mockMode ? 'mock' : 'live', name);

        const label = moeName === name ? moeName : `${moeName} (${name})`;
        if (config.mockMode) {
            debugInfo('[MoEngage:mock]', label, maskProps(payload));
            return;
        }
        debugInfo('[MoEngage]', label, maskProps(payload));
        enqueue((sdk) => sdk.trackEvent(moeName, payload));
    } catch (e) {
        debugWarn('[MoEngage] trackEvent failed', name, e);
    }
};

// ---------------------------------------------------------------------------
// Deferred events: decided on one page, sent when the user reaches the next.
// e.g. Pan_Verified_CDP is earned on the PAN page but the handover sends it
// when the user moves on to Bank — possibly after a DigiLocker full-page
// redirect, so the pending flag lives in sessionStorage. A refresh or a
// resume straight into Bank has no flag and sends nothing.
// ---------------------------------------------------------------------------
const DEFERRED_KEY = 'ekyc_moe_deferred';

const readDeferred = () => {
    try {
        const raw = window.sessionStorage.getItem(DEFERRED_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
    } catch (_) {
        return [];
    }
};

const writeDeferred = (list) => {
    try {
        if (list.length) window.sessionStorage.setItem(DEFERRED_KEY, JSON.stringify(list));
        else window.sessionStorage.removeItem(DEFERRED_KEY);
    } catch (_) { /* storage blocked — the deferred event is simply not sent */ }
};

/**
 * Mark `name` to be sent later by flushDeferredEvent(name). Repeat calls are a no-op.
 * @param {string} name
 * @returns {void}
 */
export const deferEvent = (name) => {
    try {
        if (!config.enabled || !name) return;
        const list = readDeferred();
        if (!list.includes(name)) writeDeferred([...list, name]);
    } catch (e) {
        debugWarn('[MoEngage] deferEvent failed', name, e);
    }
};

/**
 * Send `name` now if deferEvent(name) marked it, then clear the mark.
 * @param {string} name
 * @param {Record<string, unknown>} [props]
 * @returns {void}
 */
export const flushDeferredEvent = (name, props) => {
    try {
        if (!config.enabled || !name) return;
        const list = readDeferred();
        if (!list.includes(name)) return;
        writeDeferred(list.filter((n) => n !== name));
        trackEvent(name, props);
    } catch (e) {
        debugWarn('[MoEngage] flushDeferredEvent failed', name, e);
    }
};

/**
 * Clear a deferEvent(name) mark without sending — for when the call site has
 * just sent the event itself, so a later flushDeferredEvent(name) must not repeat it.
 * @param {string} name
 * @returns {void}
 */
export const cancelDeferredEvent = (name) => {
    try {
        if (!config.enabled || !name) return;
        const list = readDeferred();
        if (list.includes(name)) writeDeferred(list.filter((n) => n !== name));
    } catch (e) {
        debugWarn('[MoEngage] cancelDeferredEvent failed', name, e);
    }
};

/** Drop all pending deferred events (journey reset / logout). */
export const resetDeferredEvents = () => writeDeferred([]);

/**
 * Like trackEvent, but fires at most once per page load for `key`
 * (default: the event name). Use for *_loaded events that can re-render.
 * @param {string} name
 * @param {Record<string, unknown>} [props]
 * @param {string} [key]
 * @returns {void}
 */
export const trackOnce = (name, props, key) => {
    try {
        const k = key || name;
        if (firedOnce.has(k)) return;
        firedOnce.add(k);
        trackEvent(name, props);
    } catch (e) {
        debugWarn('[MoEngage] trackOnce failed', name, e);
    }
};

/** Forget trackOnce keys (e.g. on journey reset / logout). */
export const resetTrackOnce = () => {
    firedOnce.clear();
};
